import React, { useEffect, useState } from 'react';
import { Box, Button, makeStyles, Modal, TextField, Typography } from '@material-ui/core';

import { searchApi as api } from 'api/search';
import { DataTable, Notifications } from 'shared';

const useStyles = makeStyles((theme) => ({
    modal: {
        position: 'absolute',
        width: 600,
        backgroundColor: theme.palette.background.paper,
        border: '2px solid #000',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
    },
}));

function AddVectorSearchConfigModal({ existingSearchStrings, onAdd, onClose, open }) {
    const classes = useStyles();
    const initialConfigState = {
        searchString: '',
        similarityThreshold: '',
        l2Boost: '',
        l1Boost: '',
        nameBoost: '',
        brandBoost: '',
        numResults: '',
    };
    const [newConfig, setNewConfig] = useState(initialConfigState);
    const [error, setError] = useState('');

    const handleNewConfigChange = (e) => {
        const { name, value } = e.target;
        setNewConfig((prev) => ({ ...prev, [name]: value }));
    };

    const handleAddClick = () => {
        if (!newConfig.searchString) {
            setError('Search String is required.');
            return;
        }
        if (existingSearchStrings.includes(newConfig.searchString)) {
            setError('Search String already exists.');
            return;
        }
        setError('');

        const configToSend = {};
        Object.keys(newConfig).forEach((key) => {
            if (newConfig[key] !== '' && newConfig[key] !== null) {
                configToSend[key] = newConfig[key];
            }
        });

        onAdd(configToSend)
            .then(() => {
                handleClose();
            })
            .catch(() => {
                // Error is handled by the parent component's notification
                // We just need to prevent the modal from closing
            });
    };

    const handleClose = () => {
        setNewConfig(initialConfigState);
        setError('');
        onClose();
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <div className={classes.modal}>
                <Typography variant="h6">Add New Vector Search Config</Typography>
                {error && (
                    <Typography color="error" variant="body2">
                        {error}
                    </Typography>
                )}
                <TextField
                    name="searchString"
                    label="Search String"
                    value={newConfig.searchString}
                    onChange={handleNewConfigChange}
                    required
                    fullWidth
                    margin="normal"
                />
                <TextField
                    name="similarityThreshold"
                    label="Similarity Threshold"
                    value={newConfig.similarityThreshold}
                    onChange={handleNewConfigChange}
                    fullWidth
                    margin="normal"
                    type="number"
                />
                <TextField
                    name="l2Boost"
                    label="L2 Boost"
                    value={newConfig.l2Boost}
                    onChange={handleNewConfigChange}
                    fullWidth
                    margin="normal"
                    type="number"
                />
                <TextField
                    name="l1Boost"
                    label="L1 Boost"
                    value={newConfig.l1Boost}
                    onChange={handleNewConfigChange}
                    fullWidth
                    margin="normal"
                    type="number"
                />
                <TextField
                    name="nameBoost"
                    label="Name Boost"
                    value={newConfig.nameBoost}
                    onChange={handleNewConfigChange}
                    fullWidth
                    margin="normal"
                    type="number"
                />
                <TextField
                    name="brandBoost"
                    label="Brand Boost"
                    value={newConfig.brandBoost}
                    onChange={handleNewConfigChange}
                    fullWidth
                    margin="normal"
                    type="number"
                />
                <TextField
                    name="numResults"
                    label="Num Results"
                    value={newConfig.numResults}
                    onChange={handleNewConfigChange}
                    fullWidth
                    margin="normal"
                    type="number"
                />
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button onClick={handleClose} sx={{ mr: 1 }}>
                        Cancel
                    </Button>
                    <Button onClick={handleAddClick} color="primary" variant="contained">
                        Add
                    </Button>
                </Box>
            </div>
        </Modal>
    );
}

function VectorSearchManagement({}) {
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: '', level: '' });
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleAddConfig = async (config) => {
        try {
            await api.addVectorSearchConfig(config);
            getCurrentConfigList();
            setNotification({ text: 'Successfully added config.', level: 'success' });
            return Promise.resolve();
        } catch (error) {
            setNotification({ text: 'Failed to add config.', level: 'error' });
            return Promise.reject();
        }
    };

    const getCurrentConfigList = () => {
        api.getVectorSearchConfigList().then((res) => {
            if (res && res.data && res.data.rows) {
                setRows(res.data.rows);
            } else {
                setRows([]);
            }
        });
    };

    useEffect(() => {
        getCurrentConfigList();
    }, []);

    const handleRemoveSearchString = async (searchStr) => {
        try {
            await api.removeVectorSearchConfig(searchStr);
            getCurrentConfigList();
            setNotification({ text: 'Successfully removed.', level: 'success' });
        } catch (error) {
            setNotification({ text: 'Failed to remove config.', level: 'error' });
        }
    };

    const cols = [
        {
            accessor: 'searchString',
            Header: 'Search String',
        },
        {
            accessor: 'similarityThreshold',
            Header: 'Similarity Threshold',
        },
        {
            accessor: 'l2Boost',
            Header: 'L2 Boost',
        },
        {
            accessor: 'l1Boost',
            Header: 'L1 Boost',
        },
        {
            accessor: 'nameBoost',
            Header: 'Name Boost',
        },
        {
            accessor: 'brandBoost',
            Header: 'Brand Boost',
        },
        {
            accessor: 'numResults',
            Header: 'Num Results',
        },
        {
            Header: 'Actions',
            id: 'actions',
            Cell: ({ row }) => (
                <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveSearchString(row.original.searchString);
                    }}
                >
                    Remove
                </Button>
            ),
        },
    ];

    return (
        <>
            <Notifications options={notification} />
            <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                        Vector Search Management
                    </Typography>
                    <Button variant="contained" color="primary" onClick={handleOpenModal}>
                        Add Config
                    </Button>
                </Box>
                <DataTable editable columns={cols} data={rows} saveEdit={async ({ field, id, value }) => true} />
            </Box>
            <AddVectorSearchConfigModal
                open={isModalOpen}
                onClose={handleCloseModal}
                onAdd={handleAddConfig}
                existingSearchStrings={rows.map((row) => row.searchString)}
            />
        </>
    );
}

export { VectorSearchManagement };
